import React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import SidePanel from 'src/components/sidePanel/SidePanel';
import CashCollectionSidePanel from 'src/features/cashCollections/components/CashCollectionSidePanel';
import { CellCashCollectionActions } from 'src/features/cashCollections/components/CellCashCollectionActions';
import {
    type TotalAmounts,
    useListParkingCashCollections,
} from 'src/features/cashCollections/hooks/useListParkingCashCollections';
import { badgeCollectionStatusOptions } from 'src/features/cashCollections/utils/constants';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useListPayStations } from 'src/features/payStations/api/useListPaystations';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import StatisticsCards from 'src/features/statistics/components/StatisticsCards';
import { statisticsMessages } from 'src/features/statistics/utils';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import { useSingleRowSelection } from 'src/hooks/useSingleRowSelection';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellAction } from 'src/registry/components/table/cells/CellAction';
import DataTableColumnGenericHeaderDefinition from 'src/registry/components/table/DataTableColumnGenericHeaderDefinition';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';

import type { CashCollections as CashCollectionsType } from 'src/features/cashCollections/types/cashCollections';

export default function CashCollections() {
    const { parkingId } = useParking();
    const CASH_COLLECTION_PANEL_NAME = 'cah-collection-panel';

    const { setTitle, removeTitle } = useTitleStore();
    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });
    const { data: parkingData } = useGetParking({ parkingId });

    React.useEffect(() => {
        setTitle(formatMessage(cashCollectionsMessages.cashCollection));
        return removeTitle;
    }, []);

    const { formatMessage, formatNumber } = useIntl();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'payment_datetime', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'card_type' });

    const { data: payStationsData } = useListPayStations({});

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();

    const singleRowSelection = React.useCallback((rowId: string) => {
        setRowSelectionState({ [rowId]: true });
    }, []);

    const cashCollectionTypeTranslationMap = {
        collect: formatMessage(cashCollectionsMessages.cashCollectionCollect),
        load: formatMessage(cashCollectionsMessages.cashCollectionLoad),
    };

    const {
        data: cashCollections,
        isLoading,
        isError,
    } = useListParkingCashCollections({
        parkingId,
        date_before: dateTime.beforeDate.toISOString().split('T')[0],
        date_after: dateTime.afterDate.toISOString().split('T')[0],
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <DataTableDateDropdown
                    setValue={setDateTime}
                    inline
                    afterDate={dateTime.afterDate}
                    beforeDate={dateTime.beforeDate}
                />,
            ),
        [setDateTime, dateTime, parkingId, querySortingOptions, queryFiltersOptions],
    );

    const columnHelper = createColumnHelper<CashCollectionsType>();

    const columns = [
        columnHelper.accessor('number', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellAction
                    {...ctx}
                    sidePanelName={CASH_COLLECTION_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                    render={() => (
                        <div className="flex flex-col">
                            <span>{ctx.getValue()}</span>
                        </div>
                    )}
                />
            ),
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionNumber),
            },
            maxSize: 45,
            id: 'number',
        }),
        columnHelper.accessor('paystation.identifier', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            id: 'paystation',
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cashCollectionsMessages.cashCollectionsPayStation),
                options: payStationsData?.results.map((station) => {
                    return { value: station.id, label: station.identifier };
                }),
            },
        }),
        columnHelper.accessor('date', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionDate),
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => <CellBadge badgeOptions={badgeCollectionStatusOptions} {...props} />,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cashCollectionsMessages.cashCollectionStatus),
                badgeOptions: badgeCollectionStatusOptions,
            },
        }),
        columnHelper.accessor('total_coins_amount', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return formatNumber(row.original.total_coins_amount / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionCoins),
            },
        }),
        columnHelper.accessor('total_banknotes_amount', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return formatNumber(row.original.total_banknotes_amount / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionBanknotes),
            },
        }),
        columnHelper.accessor('total_amount', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return formatNumber(row.original.total_amount / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                });
            },
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.cashCollectionTotalAmount),
            },
        }),
        columnHelper.accessor('created_by', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.createdBy),
            },
        }),
        columnHelper.accessor('description', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(cashCollectionsMessages.description),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellCashCollectionActions
                    {...props}
                    sidePanelName={CASH_COLLECTION_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: () => null,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                filterLabel: formatMessage(cashCollectionsMessages.cashCollectionType),
                options: Object.entries(cashCollectionTypeTranslationMap).map(([key, value]) => ({
                    value: key,
                    label: value,
                })),
            },
            id: 'type',
        }),
    ];

    const getTotalsBasedOnType = (type: 'collect' | 'load', totalField: keyof TotalAmounts) => {
        return cashCollections && parkingData
            ? formatNumber(cashCollections?.total_amounts?.[type]?.[totalField] / 100, {
                  style: 'currency',
                  currency: parkingData.default_currency,
              })
            : null;
    };

    const cardData = React.useMemo(() => {
        const totalCashCollected = getTotalsBasedOnType('collect', 'total_amount');
        const totalCoinsCollected = getTotalsBasedOnType('collect', 'total_coins_amount');
        const totalBanknotesCollected = getTotalsBasedOnType('collect', 'total_banknotes_amount');

        const totalCashLoaded = getTotalsBasedOnType('load', 'total_amount');
        const totalCoinsLoaded = getTotalsBasedOnType('load', 'total_coins_amount');
        const totalBanknotesLoaded = getTotalsBasedOnType('load', 'total_banknotes_amount');

        return [
            {
                title: formatMessage(cashCollectionsMessages.totalCashCollected),
                value: totalCashCollected,
            },
            {
                title: formatMessage(cashCollectionsMessages.totalCoinsCollected),
                value: totalCoinsCollected,
            },
            {
                title: formatMessage(cashCollectionsMessages.totalBanknotesCollected),
                value: totalBanknotesCollected,
            },
            {
                title: formatMessage(cashCollectionsMessages.totalCollectionsNumber),
                value: cashCollections?.total_amounts?.['collect']?.count || 0,
            },
            {
                title: formatMessage(cashCollectionsMessages.totalCashLoaded),
                value: totalCashLoaded,
            },
            {
                title: formatMessage(cashCollectionsMessages.totalCoinsLoaded),
                value: totalCoinsLoaded,
            },
            {
                title: formatMessage(cashCollectionsMessages.totalBanknotesLoaded),
                value: totalBanknotesLoaded,
            },
            {
                title: formatMessage(cashCollectionsMessages.totalLoadNumber),
                value: cashCollections?.total_amounts?.['load']?.count || 0,
            },
        ];
    }, [cashCollections, parkingData]);

    const getRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!cashCollections || !firstSelected) return;
        return cashCollections.results.find((payment) => payment.id.toString() === firstSelected);
    }, [cashCollections, rowSelectionState]);

    const selectedCashCollectionId = getRowData?.id ?? '';

    return (
        <SectionBlock>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/statistics/reports">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(statisticsMessages.backToAllReports)}
                </Link>
            </Button>
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(cashCollectionsMessages.allCashCollections)}</SectionBlockTitle>
                    <SectionBlockSubtitle>
                        {formatMessage(cashCollectionsMessages.cashCollectionsDescription)}
                    </SectionBlockSubtitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <StatisticsCards cardData={cardData} />
            <DataTable
                columns={columns}
                data={cashCollections}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(cashCollectionsMessages.cashCollections),
                    entryName: formatMessage(cashCollectionsMessages.collection),
                    entryNames: formatMessage(cashCollectionsMessages.collections),
                    placeholder: formatMessage(cashCollectionsMessages.cashCollection),
                    sidePanelName: CASH_COLLECTION_PANEL_NAME,
                    initialColumnVisibility: { type: false },
                }}
                isError={isError}
                isLoading={isLoading}
                rowSelection={{
                    rowSelectionState: rowSelectionState,
                    setRowSelectionState: setRowSelectionState,
                }}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            <SidePanel onClose={clearSelection} name={CASH_COLLECTION_PANEL_NAME}>
                <CashCollectionSidePanel id={selectedCashCollectionId} parkingId={parkingId} />
            </SidePanel>
        </SectionBlock>
    );
}
